import { Buffer } from "buffer";
import { type Connection, PublicKey } from "@solana/web3.js";
import { withTimeout } from "./async";
import { FEE_ROUTER_PROGRAM_ID, FRONTEND_FEE_RECIPIENT } from "./constants";

const EVENT_DISCRIMINATOR = Buffer.from("TRBFEE01");
const EVENT_LENGTH = 129;
const LOG_PREFIX = "Program data: ";
const PUBLIC_HISTORY_RPC = "api.mainnet-beta.solana.com";
const HISTORY_REQUEST_DELAY_MS = 500;

export interface RouterTradeMetric {
  signature: string;
  blockTime?: number;
  side: "buy" | "sell";
  trader: string;
  pool: string;
  mint: string;
  volumeLamports: bigint;
  tribeFeeLamports: bigint;
  interfaceFeeLamports: bigint;
}

export interface PlatformMetrics {
  traders: number;
  trades: number;
  buys: number;
  sells: number;
  volumeLamports: bigint;
  tribeFeeLamports: bigint;
  interfaceFeeLamports: bigint;
}

// Immutable on-chain totals through router signature 3J9sdoeaFK... . Keeping
// this baseline in the static client prevents a transient archive-RPC outage
// from erasing already-confirmed history for a new browser.
export const PLATFORM_METRICS_BASELINE: PlatformMetrics = {
  traders: 3,
  trades: 4,
  buys: 3,
  sells: 1,
  volumeLamports: 15_101_940_899n,
  tribeFeeLamports: 453_059_100n,
  interfaceFeeLamports: 150_019_408n,
};

function readU64(data: Uint8Array, offset: number): bigint {
  let value = 0n;
  for (let index = 7; index >= 0; index -= 1) {
    value = (value << 8n) | BigInt(data[offset + index]);
  }
  return value;
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => globalThis.setTimeout(resolve, milliseconds));
}

async function loadTransactions(
  connection: Connection,
  signatures: readonly string[],
) {
  const options = {
    commitment: "confirmed" as const,
    maxSupportedTransactionVersion: 0,
  };
  const isThrottledHistoryEndpoint =
    connection.rpcEndpoint.includes(PUBLIC_HISTORY_RPC) ||
    connection.rpcEndpoint.includes("/solana-rpc");
  if (!isThrottledHistoryEndpoint) {
    return withTimeout(
      connection.getTransactions([...signatures], options),
      15_000,
      "Platform transaction request timed out",
    );
  }

  // Solana's public endpoint counts every item in a JSON-RPC batch against its
  // method limit. Small batches can therefore return 429 even though the same
  // transactions are available when requested at a controlled rate.
  const transactions = [];
  for (let index = 0; index < signatures.length; index += 1) {
    const signature = signatures[index];
    let transaction;
    let lastError: unknown;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        transaction = await withTimeout(
          connection.getTransaction(signature, options),
          10_000,
          "Platform transaction request timed out",
        );
        lastError = undefined;
        break;
      } catch (error) {
        lastError = error;
        await delay(HISTORY_REQUEST_DELAY_MS * (attempt + 1));
      }
    }
    if (lastError) throw lastError;
    transactions.push(transaction ?? null);
    if (index < signatures.length - 1) await delay(HISTORY_REQUEST_DELAY_MS);
  }
  return transactions;
}

function decodeEvent(data: Buffer): Omit<RouterTradeMetric, "signature" | "blockTime"> | undefined {
  if (
    data.length !== EVENT_LENGTH ||
    !EVENT_DISCRIMINATOR.every((value, index) => data[index] === value)
  ) {
    return undefined;
  }
  const trader = new PublicKey(data.subarray(9, 41)).toBase58();
  return {
    side: data[8] === 1 ? "buy" : "sell",
    trader,
    pool: new PublicKey(data.subarray(41, 73)).toBase58(),
    mint: new PublicKey(data.subarray(73, 105)).toBase58(),
    volumeLamports: readU64(data, 105),
    tribeFeeLamports: readU64(data, 113),
    // A wallet cannot pay a net fee to itself. Older router builds logged the
    // theoretical amount for fee-recipient trades even though no transfer ran.
    interfaceFeeLamports:
      trader === FRONTEND_FEE_RECIPIENT.toBase58() ? 0n : readU64(data, 121),
  };
}

export function parseRouterEvents(
  logs: readonly string[],
): Array<Omit<RouterTradeMetric, "signature" | "blockTime">> {
  const stack: string[] = [];
  const events: Array<Omit<RouterTradeMetric, "signature" | "blockTime">> = [];
  for (const line of logs) {
    const invoke = /^Program (\S+) invoke \[(\d+)]$/.exec(line);
    if (invoke) {
      const depth = Number(invoke[2]);
      stack.length = Math.max(0, depth - 1);
      stack.push(invoke[1]);
      continue;
    }
    const end = /^Program (\S+) (?:success|failed:)/.exec(line);
    if (end) {
      if (stack.at(-1) === end[1]) stack.pop();
      continue;
    }
    if (
      !line.startsWith(LOG_PREFIX) ||
      stack.at(-1) !== FEE_ROUTER_PROGRAM_ID.toBase58()
    ) {
      continue;
    }
    try {
      const event = decodeEvent(Buffer.from(line.slice(LOG_PREFIX.length), "base64"));
      if (event) events.push(event);
    } catch {
      // Ignore malformed logs; only the router's fixed event format is counted.
    }
  }
  return events;
}

export function aggregateRouterMetrics(metrics: readonly RouterTradeMetric[]): PlatformMetrics {
  const traders = new Set<string>();
  let buys = 0;
  let sells = 0;
  let volumeLamports = 0n;
  let tribeFeeLamports = 0n;
  let interfaceFeeLamports = 0n;
  for (const metric of metrics) {
    traders.add(metric.trader);
    if (metric.side === "buy") buys += 1;
    else sells += 1;
    volumeLamports += metric.volumeLamports;
    tribeFeeLamports += metric.tribeFeeLamports;
    interfaceFeeLamports += metric.interfaceFeeLamports;
  }
  return {
    traders: traders.size,
    trades: metrics.length,
    buys,
    sells,
    volumeLamports,
    tribeFeeLamports,
    interfaceFeeLamports,
  };
}

export function mostCompleteMetrics(
  candidates: readonly PlatformMetrics[],
): PlatformMetrics | undefined {
  return candidates.reduce<PlatformMetrics | undefined>((best, candidate) => {
    if (!best) return candidate;
    if (candidate.trades !== best.trades) {
      return candidate.trades > best.trades ? candidate : best;
    }
    return candidate.volumeLamports > best.volumeLamports ? candidate : best;
  }, undefined);
}

export async function loadRouterMetrics(connection: Connection): Promise<PlatformMetrics> {
  const signatures = await withTimeout(
    connection.getSignaturesForAddress(
      FEE_ROUTER_PROGRAM_ID,
      { limit: 1_000 },
      "confirmed",
    ),
    12_000,
    "Platform activity request timed out",
  );
  const successful = signatures.filter((entry) => !entry.err);
  const metrics: RouterTradeMetric[] = [];
  for (let offset = 0; offset < successful.length; offset += 25) {
    const chunk = successful.slice(offset, offset + 25);
    const transactions = await loadTransactions(
      connection,
      chunk.map((entry) => entry.signature),
    );
    transactions.forEach((transaction, index) => {
      if (!transaction || transaction.meta?.err) return;
      const entry = chunk[index];
      const events = parseRouterEvents(transaction.meta?.logMessages || []);
      for (const event of events) {
        metrics.push({
          ...event,
          signature: entry.signature,
          blockTime: transaction.blockTime ?? entry.blockTime ?? undefined,
        });
      }
    });
  }
  return aggregateRouterMetrics(metrics);
}
